import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Camera, Database, FileText, Loader2, Shield } from 'lucide-react';
import { TabbedSidebarLayout, type TabNavItem } from '../../components/TabbedSidebarLayout';
import { LoginButton } from '../../components/LoginButton';
import { LangToggle } from '../../components/LangToggle';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNoticeAuth } from './NoticeAuthContext';
import { IS_DEMO, IS_DEV, IS_SANDBOX } from '../../apiBase';

// Sidebar shell for NoticeIA: the shared tabbed layout (desktop rail +
// collapsible mobile drawer). NoticeIA has its own account pool, so the profile
// menu + login gate here use the NoticeIA session (useNoticeAuth), not Lumna's.
// The top-left brand is NoticeIA's wordmark, linking to its welcome page.
export function NoticeLayout() {
  const { user, isAuthenticated, isLoading, login, logout } = useNoticeAuth();
  const { t } = useLanguage();

  // WHICH APP THE TAB IS, for the owner, who has prod and dev open side by side (Louis 2026-10-29).
  // Nobody else is told: to them there is one app.
  const prod = !IS_DEV && !IS_DEMO && !IS_SANDBOX && !!user?.is_owner;
  useEffect(() => {
    document.title = IS_DEV ? 'NoticeIA (dev)' : prod ? 'NoticeIA (prod)' : 'NoticeIA';
  }, [prod]);

  // Signed out (and during the initial auth check): a minimal full-screen page
  // with no app shell / sidebar — just the brand, a one-line pitch, and Google
  // sign-in. The authoring app is allowlisted, so there is nothing to show a
  // visitor until they are in. The blueprint backdrop itself lives on `html, body`
  // (index.css) — this page and TabbedSidebarLayout below both stay transparent and
  // let it show through, rather than each carrying its own.
  if (!isAuthenticated) {
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center text-slate-100">
        <div className="absolute right-4 top-4">
          <LangToggle />
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        ) : (
          <>
            <span className="text-2xl font-bold">NoticeIA</span>
            <LoginButton redirectTo="/" login={login} />
          </>
        )}
      </div>
    );
  }

  // No Home entry: the page is what the brand link opens (top-left, and "/" redirects
  // to it), so a tab for it was a second door onto the room you are already standing in.
  // NO ADMIN IN THE DEMO: the accounts, the spending and the pull-from-prod button are not
  // the product, and a door the demo has no business opening. Gone from the router as
  // well (NoticeApp), so this is what is offered rather than the whole of what is reachable.
  // (A Tests tab, the 3D workbench, was here too; removed from the app, Louis 2026-10-23.)
  //
  // AND THE SANDBOX SHOWS ANOTHER. It is a copy to try design ideas in — on the READER,
  // which is the thing being designed — so the parts database is a door away from what it
  // is for. In its place, Saves: what the reader looked like, kept across the resets that
  // take everything else.
  const nav: TabNavItem[] = [
    // One entry for the manuals: it opens the list, and stays lit while one of them
    // is open (/view/<slug> is under it, and NavLink matches by prefix). Two entries
    // was two names for one place — the reader could not reach the list, and the list
    // could not say which manual the reader had open.
    { to: '/view', label: t('notice.nav.viewer'), icon: FileText },
    ...(IS_SANDBOX
      ? [{ to: '/saves', label: t('notice.nav.saves'), icon: Camera } as TabNavItem]
      : [{ to: '/catalog', label: t('notice.nav.catalog'), icon: Database } as TabNavItem]),
    // Admin-only section (the admin console), set off by a divider.
    ...(user?.is_admin && !IS_DEMO
      ? [
          { to: '/admin', label: t('notice.nav.admin'), icon: Shield, dividerBefore: true } as TabNavItem,
        ]
      : []),
  ];

  return (
    <TabbedSidebarLayout
      title="NoticeIA"
      nav={nav}
      langToggle
      brand={{ to: '/', label: 'NoticeIA' }}
      contentClassName="flex flex-col text-slate-100"
      auth={{ user, isAuthenticated, isLoading, logout, login }}
    >
      <Outlet />
    </TabbedSidebarLayout>
  );
}
